const CURRENCY = 'XTS';

const push = (event, params = {}) => {
   if (typeof window === 'undefined') return;
   window.dataLayer = window.dataLayer || [];
   // Clear previous ecommerce object before every push — required by GA4 to
   // prevent stale item data from the last event bleeding into this one.
   if (params.ecommerce) window.dataLayer.push({ ecommerce: null });
   window.dataLayer.push({ event, ...params });
};

// Available outside React components for UI mounted imperatively (quick views)
// and browser-level interaction listeners.
export const trackAnalyticsEvent = (event, params = {}) => push(event, params);

const CATEGORY_KEYS = ['item_category', 'item_category2', 'item_category3', 'item_category4', 'item_category5'];

const toItem = ({ id, name, company, categories, quantity, index, listId, listName } = {}) => {
   const categoryFields = {};
   if (Array.isArray(categories)) {
      categories.slice(0, 5).forEach((cat, i) => {
         categoryFields[CATEGORY_KEYS[i]] = cat.categoryTitle ?? cat;
      });
   }
   return {
      item_id: id,
      item_name: name,
      item_brand: company ?? '',
      ...categoryFields,
      ...(quantity != null && { quantity }),
      ...(index != null && { index }),
      ...(listId != null && { item_list_id: listId }),
      ...(listName != null && { item_list_name: listName }),
      price: 1,
   };
};

const useAnalytics = () => {
   // ─── Forms ───────────────────────────────────────────────────────────────
   const trackFormStart = (formId) =>
      push('form_start', { form_id: formId });

   const trackFormSubmit = (formId) =>
      push('form_submit', { form_id: formId });

   const trackFormSuccess = (formId, meta = {}) =>
      push('form_success', { form_id: formId, ...meta });

   const trackFormError = (formId, errorMessage) =>
      push('form_error', { form_id: formId, error_message: errorMessage });

   // ─── E-commerce ──────────────────────────────────────────────────────────

   /**
    * Fire when a list of products is rendered/visible.
    * Re-fire whenever the visible set changes (filters applied/cleared).
    * @param {Array<{ id, name, company, categories, index }>} items
    * @param {string} [listId]
    * @param {string} [listName]
    */
   const trackViewItemList = (items, listId = 'showcase_grid', listName = 'Showcase') =>
      push('view_item_list', {
         ecommerce: {
            item_list_id: listId,
            item_list_name: listName,
            items: items.map(({ id, name, company, categories, index }) =>
               toItem({ id, name, company, categories, index, listId, listName })
            ),
         },
      });

   /**
    * Fire when a user clicks a product to navigate to its detail page.
    * @param {{ id, name, company, categories, index }} item
    * @param {string} [listId]
    * @param {string} [listName]
    */
   const trackSelectItem = ({ id, name, company, categories, index, sourceCompanyId }, listId = 'showcase_grid', listName = 'Showcase') =>
      push('select_item', {
         product_card_source: listId,
         ...(sourceCompanyId && { source_company_id: sourceCompanyId }),
         ecommerce: {
            item_list_id: listId,
            item_list_name: listName,
            items: [toItem({ id, name, company, categories, index, listId, listName })],
         },
      });

   /**
    * Fire when a product detail page is viewed.
    * @param {{ id, name, company, categories, index }} item
    */
   const trackViewItem = ({ id, name, company, categories, index }) =>
      push('view_item', {
         ecommerce: {
            currency: CURRENCY,
            value: 1,
            items: [toItem({ id, name, company, categories, index })],
         },
      });

   /**
    * Fire when a product is added to the cart.
    * @param {{ id, name, company, categories }} item
    * @param {number} quantity
    * @param {'product_card'|'quick_view'|'product_page'} [source]
    */
   const trackAddToCart = ({ id, name, company, categories }, quantity = 1, source = 'product_card') =>
      push('add_to_cart', {
         ecommerce: {
            currency: CURRENCY,
            value: quantity,
            items: [toItem({ id, name, company, categories, quantity })],
         },
         atc_source: source,
      });

   /**
    * Fire when an item is removed from the cart.
    * @param {{ id, name, company, categories }} item
    * @param {number} quantity
    */
   const trackRemoveFromCart = ({ id, name, company, categories }, quantity = 1) =>
      push('remove_from_cart', {
         ecommerce: {
            currency: CURRENCY,
            value: quantity,
            items: [toItem({ id, name, company, categories, quantity })],
         },
      });

   /**
    * Fire when the cart quick-view panel is opened.
    * @param {Array<{ id, name, company, categories, quantity }>} items
    */
   const trackViewCart = (items = []) => {
      const mapped = items.map(({ id, name, company, categories, quantity = 1 }) =>
         toItem({ id, name, company, categories, quantity })
      );
      const value = mapped.reduce((sum, i) => sum + (i.quantity ?? 1), 0);
      push('view_cart', {
         ecommerce: {
            currency: CURRENCY,
            value,
            items: mapped,
         },
      });
   };

   /**
    * Fire when the checkout form submission is confirmed by Postmark.
    * Uses Postmark MessageID as transaction_id — only fires for the checkout form.
    * @param {Array<{ id, name, company, categories, quantity }>} items
    * @param {string|null} transactionId  Postmark MessageID
    */
   const trackPurchase = (items = [], transactionId = null) => {
      const mapped = items.map(({ id, name, company, categories, quantity = 1 }) =>
         toItem({ id, name, company, categories, quantity })
      );
      const value = mapped.reduce((sum, i) => sum + (i.quantity ?? 1), 0);
      push('purchase', {
         ecommerce: {
            currency: CURRENCY,
            value,
            transaction_id: transactionId ?? `checkout-${Date.now()}`,
            items: mapped,
         },
      });
   };

   /**
    * Fire when the user clicks the Checkout CTA inside the cart panel.
    * @param {Array<{ id, name, company, categories, quantity }>} items
    */
   const trackBeginCheckout = (items = []) => {
      const mapped = items.map(({ id, name, company, categories, quantity = 1 }) =>
         toItem({ id, name, company, categories, quantity })
      );
      const value = mapped.reduce((sum, i) => sum + (i.quantity ?? 1), 0);
      push('begin_checkout', {
         ecommerce: {
            currency: CURRENCY,
            value,
            items: mapped,
         },
      });
   };

   /**
    * Fire when a Quick View popup is opened from a product card.
    * @param {{ id, name, company }} item
    */
   const trackQuickView = ({ id, name, company }, source = 'showcase_grid') =>
      push('quick_view_open', {
         item_id: id,
         item_name: name,
         item_brand: company,
         quick_view_source: source,
      });

   /**
    * Fire when the filter panel is applied.
    * @param {{ companies: string[], categories: string[] }} filters
    * @param {number} resultCount
    */
   const trackFilterApply = (filters, resultCount) =>
      push('filter_apply', {
         filter_companies: filters.companies.join(','),
         filter_categories: filters.categories.join(','),
         filter_result_count: resultCount,
      });

   /** Fire when filters are cleared. */
   const trackFilterClear = () => push('filter_clear', {});

   // ─── Homepage engagement ─────────────────────────────────────────────────

   /**
    * Fire when any binary toggle is flipped on the homepage.
    * @param {string} toggleId  e.g. 'sunglasses'
    * @param {boolean} active   new state after the toggle
    */
   const trackToggle = (toggleId, active) =>
      push('toggle', { toggle_id: toggleId, toggle_active: active });

   /**
    * Fire when a social/external link is clicked.
    * @param {'github'|'linkedin'|'personal'|string} platform
    * @param {string} url
    */
   const trackSocialClick = (platform, url) =>
      push('social_click', { social_platform: platform, link_url: url });

   // ─── Generic ─────────────────────────────────────────────────────────────
   const trackPageView = (path, title) =>
      push('page_view', { page_path: path, page_title: title });

   const trackEvent = (eventName, params = {}) =>
      push(eventName, params);

   return {
      // forms
      trackFormStart,
      trackFormSubmit,
      trackFormSuccess,
      trackFormError,
      // ecommerce
      trackPurchase,
      trackViewItemList,
      trackSelectItem,
      trackViewItem,
      trackAddToCart,
      trackRemoveFromCart,
      trackViewCart,
      trackBeginCheckout,
      trackQuickView,
      trackFilterApply,
      trackFilterClear,
      // homepage
      trackToggle,
      trackSocialClick,
      // generic
      trackPageView,
      trackEvent,
   };
};

export default useAnalytics;
